/**
 * GithubRepoVariablePicker - Custom Backstage Scaffolder Field Extension
 *
 * A dropdown picker that reads a GitHub repository variable containing
 * comma-separated values and displays them as selectable options.
 *
 * Usage in template.yaml:
 *   ui:field: GithubRepoVariablePicker
 *   ui:options:
 *     repoParam: business_unit_repo    # Reference to another form field containing the repo name
 *     owner: badal-io                  # Optional, defaults to badal-io
 *     variableName: ENVIRONMENTS       # Name of the repository variable
 *     separator: ','                   # Optional, defaults to ','
 */

import {
  FormControl,
  TextField,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useGithubRepoVariable } from './shared';

export interface GithubRepoVariablePickerOptions {
  owner?: string;
  repoParam?: string;
  repo?: string;
  variableName: string;
  separator?: string;
  multiple?: boolean;
  [key: string]: string | number | boolean | object | any[] | null | undefined;
}

export const GithubRepoVariablePicker = (
  props: FieldExtensionComponentProps<string | string[], GithubRepoVariablePickerOptions>,
) => {
  const { onChange, rawErrors, required, formData, formContext, uiSchema, schema } = props;

  const owner = uiSchema?.['ui:options']?.owner || 'badal-io';
  const repoParam = uiSchema?.['ui:options']?.repoParam;
  const staticRepo = uiSchema?.['ui:options']?.repo;
  const variableName = uiSchema?.['ui:options']?.variableName || '';
  const separator = uiSchema?.['ui:options']?.separator || ',';
  const multiple = uiSchema?.['ui:options']?.multiple || false;

  const repoName = repoParam
    ? (formContext?.formData as Record<string, unknown>)?.[repoParam] as string
    : staticRepo;

  const { options, loading, error } = useGithubRepoVariable(
    owner,
    repoName,
    variableName,
    separator,
  );

  if (!repoName) {
    return (
      <FormControl fullWidth margin="normal">
        <TextField
          label={schema.title || 'Select Option'}
          disabled
          value=""
          helperText="Please select a repository first"
          variant="outlined"
        />
      </FormControl>
    );
  }

  if (!variableName) {
    return (
      <FormControl fullWidth margin="normal" error>
        <TextField
          label={schema.title || 'Select Option'}
          disabled
          value=""
          helperText="Configuration error: variableName not specified"
          error
          variant="outlined"
        />
      </FormControl>
    );
  }

  if (loading) {
    return (
      <FormControl fullWidth margin="normal">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CircularProgress size={20} />
          <span>Loading {variableName} from {repoName}...</span>
        </div>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth margin="normal" error>
        <TextField
          label={schema.title || 'Select Option'}
          disabled
          value=""
          helperText={error}
          error
          variant="outlined"
        />
      </FormControl>
    );
  }

  if (options.length === 0) {
    return (
      <FormControl fullWidth margin="normal">
        <TextField
          label={schema.title || 'Select Option'}
          disabled
          value=""
          helperText={`No options found in variable '${variableName}'`}
          variant="outlined"
        />
      </FormControl>
    );
  }

  if (multiple) {
    const selectedValues = Array.isArray(formData) ? formData : [];
    return (
      <FormControl fullWidth margin="normal" required={required} error={rawErrors && rawErrors.length > 0}>
        <Autocomplete
          id="github-repo-variable-picker-multiple"
          multiple
          options={options}
          value={selectedValues}
          onChange={(_event, value) => onChange(value)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={schema.title || 'Select Options'}
              required={required}
              helperText={
                (rawErrors && rawErrors.length > 0 && rawErrors.join(', ')) ||
                schema.description ||
                uiSchema?.['ui:help']
              }
              error={rawErrors && rawErrors.length > 0}
              variant="outlined"
            />
          )}
        />
      </FormControl>
    );
  }

  const selectedValue = typeof formData === 'string' ? formData : '';
  return (
    <FormControl fullWidth margin="normal" required={required} error={rawErrors && rawErrors.length > 0}>
      <Autocomplete
        id="github-repo-variable-picker"
        options={options}
        value={selectedValue || null}
        onChange={(_event, value) => onChange(value || '')}
        renderInput={(params) => (
          <TextField
            {...params}
            label={schema.title || 'Select Option'}
            required={required}
            helperText={
              (rawErrors && rawErrors.length > 0 && rawErrors.join(', ')) ||
              schema.description ||
              uiSchema?.['ui:help']
            }
            error={rawErrors && rawErrors.length > 0}
            variant="outlined"
          />
        )}
      />
    </FormControl>
  );
};

export default GithubRepoVariablePicker;
